import React, { useState, useEffect, useRef } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    FaSearch, FaPlus, FaEdit, FaTrash, FaEye, FaFileAlt, FaChartBar, FaClipboardList
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';
import { getActionErrorMessage } from '../utils/errorHandler';
import FormSubmissionViewer from './FormSubmissionViewer';
import FormAnalytics from './FormAnalytics';
import LoadingSpinner from './LoadingSpinner';
import {
    FORM_TYPES, getFormTypeRule, resolveMultipleSubmissions,
    getAttendanceFixedFields, isAttendanceFixedFieldId,
    ATTENDANCE_STUDY_TYPE_FIELD_ID, ATTENDANCE_DAYS_FIELD_ID
} from '../utils/formTypes';
import './FormBuilder.css';

// Every attendance template always carries the fixed study-type + calendar
// fields up front; anything else in `fields` is whatever the admin added on
// top of them. Used on create/edit/preview load and whenever form_type changes.
const reconcileFormFields = (formType, fields) => {
    const withoutFixed = (fields || []).filter((f) => !isAttendanceFixedFieldId(f.id));
    return formType === 'attendance' ? [...getAttendanceFixedFields(), ...withoutFixed] : withoutFixed;
};

// Fixed, non-removable row for the attendance form's built-in fields. No drag
// handle, no remove button, no editable inputs — just a clear description of
// what students will see, since these fields can't be changed here.
const LockedFieldRow = ({ field }) => (
    <div className="field-editor field-editor--locked">
        <div className="field-header field-header--locked">
            <span className="locked-field-badge">Fixed</span>
            <span className="field-type">{field.type === 'attendance_calendar' ? 'Multi-day calendar' : field.type}</span>
        </div>
        <div className="field-config field-config--locked">
            <div className="locked-field-label">{field.label}</div>
            {field.help_text && <div className="locked-field-help">{field.help_text}</div>}
            {field.id === ATTENDANCE_STUDY_TYPE_FIELD_ID && (
                <div className="locked-field-preview-options">
                    {field.options.map((option) => (
                        <span key={option.value} className="locked-field-chip">{option.label}</span>
                    ))}
                </div>
            )}
            {field.id === ATTENDANCE_DAYS_FIELD_ID && (
                <div className="locked-field-calendar-hint">
                    📅 Students pick any number of days across any month or year, with a start/end time per day.
                </div>
            )}
        </div>
    </div>
);

// Sortable Field Editor Component
const SortableFieldEditor = ({ field, index, updateField, removeField }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`field-editor ${isDragging ? 'dragging' : ''}`}
        >
            <div className="field-header" {...attributes} {...listeners}>
                <span className="drag-handle">≡</span>
                <span className="field-type">{field.type}</span>
                <button
                    onClick={() => removeField(field.id)}
                    className="btn-icon remove-field"
                >
                    ×
                </button>
            </div>

            <div className="field-config">
                <div className="config-row">
                    <label>Label:</label>
                    <input
                        type="text"
                        value={field.label}
                        onChange={(e) => updateField(field.id, { label: e.target.value })}
                        className="config-input"
                    />
                </div>

                <div className="config-row">
                    <label>Placeholder:</label>
                    <input
                        type="text"
                        value={field.placeholder || ''}
                        onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                        className="config-input"
                    />
                </div>

                <div className="config-row">
                    <label>Help Text:</label>
                    <input
                        type="text"
                        value={field.help_text || ''}
                        onChange={(e) => updateField(field.id, { help_text: e.target.value })}
                        className="config-input"
                    />
                </div>

                <div className="config-row">
                    <label>
                        <input
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => updateField(field.id, { required: e.target.checked })}
                        />
                        Required
                    </label>
                </div>

                {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                    <div className="config-row">
                        <label>Options:</label>
                        <div className="options-editor">
                            {field.options?.map((option, optIndex) => (
                                <div key={optIndex} className="option-row">
                                    <input
                                        type="text"
                                        value={option.value || option}
                                        onChange={(e) => {
                                            const newOptions = [...field.options];
                                            if (typeof option === 'object') {
                                                newOptions[optIndex] = { ...option, value: e.target.value };
                                            } else {
                                                newOptions[optIndex] = { value: e.target.value, label: e.target.value };
                                            }
                                            updateField(field.id, { options: newOptions });
                                        }}
                                        className="option-input"
                                        placeholder="Value"
                                    />
                                    <input
                                        type="text"
                                        value={option.label || option}
                                        onChange={(e) => {
                                            const newOptions = [...field.options];
                                            if (typeof option === 'object') {
                                                newOptions[optIndex] = { ...option, label: e.target.value };
                                            } else {
                                                newOptions[optIndex] = { value: option, label: e.target.value };
                                            }
                                            updateField(field.id, { options: newOptions });
                                        }}
                                        className="option-input"
                                        placeholder="Label"
                                    />
                                    <button
                                        onClick={() => {
                                            const newOptions = field.options.filter((_, i) => i !== optIndex);
                                            updateField(field.id, { options: newOptions });
                                        }}
                                        className="btn-icon remove-option"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={() => {
                                    const newOptions = [...(field.options || []), { 
                                        value: `option_${(field.options?.length || 0) + 1}`, 
                                        label: `Option ${(field.options?.length || 0) + 1}` 
                                    }];
                                    updateField(field.id, { options: newOptions });
                                }}
                                className="btn-secondary add-option"
                            >
                                Add Option
                            </button>
                        </div>
                    </div>
                )}

                {(field.type === 'text' || field.type === 'textarea') && (
                    <>
                        <div className="config-row">
                            <label>Min Length:</label>
                            <input
                                type="number"
                                value={field.min_length || ''}
                                onChange={(e) => updateField(field.id, { min_length: parseInt(e.target.value) || null })}
                                className="config-input"
                            />
                        </div>
                        <div className="config-row">
                            <label>Max Length:</label>
                            <input
                                type="number"
                                value={field.max_length || ''}
                                onChange={(e) => updateField(field.id, { max_length: parseInt(e.target.value) || null })}
                                className="config-input"
                            />
                        </div>
                    </>
                )}

                {field.type === 'number' && (
                    <>
                        <div className="config-row">
                            <label>Min Value:</label>
                            <input
                                type="number"
                                value={field.min_value || ''}
                                onChange={(e) => updateField(field.id, { min_value: parseFloat(e.target.value) || null })}
                                className="config-input"
                            />
                        </div>
                        <div className="config-row">
                            <label>Max Value:</label>
                            <input
                                type="number"
                                value={field.max_value || ''}
                                onChange={(e) => updateField(field.id, { max_value: parseFloat(e.target.value) || null })}
                                className="config-input"
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// Draggable Field Type Component
const DraggableFieldType = ({ fieldType, onAdd }) => {
    const handleClick = () => {
        onAdd(fieldType.type);
    };

    return (
        <div
            className="field-type-item"
            onClick={handleClick}
        >
            <span className="field-icon">{fieldType.icon}</span>
            <span className="field-label">{fieldType.label}</span>
        </div>
    );
};

const FormBuilder = ({ selectedTemplate, shouldNavigateToBuilder, onNavigationComplete, onClose }) => {
    const [activeTab, setActiveTab] = useState('templates');
    const [templates, setTemplates] = useState([]);
    const [localSelectedTemplate, setLocalSelectedTemplate] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [previewMode, setPreviewMode] = useState(false);
    const [showSubmissions, setShowSubmissions] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    
    // Form builder state
    const [formFields, setFormFields] = useState([]);
    const [formConfig, setFormConfig] = useState({
        name: '',
        description: '',
        form_type: 'assignment',
        submit_button_text: 'Submit',
        success_message: 'Form submitted successfully!',
        allow_multiple_submissions: false,
        is_active: true,
        max_submissions: null
    });
    
    // Effect to handle template selection from parent
    useEffect(() => {
        if (selectedTemplate) {
            loadTemplateData(selectedTemplate);
            setActiveTab('builder');
        }
    }, [selectedTemplate]);

    // Effect to handle navigation to builder from parent
    useEffect(() => {
        if (shouldNavigateToBuilder) {
            handleCreateNew();
            if (onNavigationComplete) {
                onNavigationComplete();
            }
        }
    }, [shouldNavigateToBuilder, onNavigationComplete]);

    const loadTemplateData = (template) => {
        if (template.fields) {
            setFormFields(template.fields.map(field => ({
                ...field,
                id: field.id || `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            })));
        }
        
        // Handle both old and new data structures
        const settings = template.settings || {};
        
        setFormConfig({
            name: template.name || settings.title || '',
            description: template.description || settings.description || '',
            form_type: template.form_type || 'assignment',
            submit_button_text: template.submit_button_text || settings.submit_button_text || 'Submit',
            success_message: template.success_message || settings.success_message || 'Form submitted successfully!',
            // Normalised against the form type so a legacy template saved before the
            // policy existed can't display a value the server would refuse to honour.
            allow_multiple_submissions: resolveMultipleSubmissions(
                template.form_type || 'assignment',
                template.allow_multiple_submissions !== undefined
                    ? template.allow_multiple_submissions
                    : settings.allow_multiple_submissions
            ),
            is_active: template.is_active !== undefined ? template.is_active : true,
            max_submissions: template.max_submissions || null
        });
        
        setLocalSelectedTemplate(template);
        setIsEditing(true);
    };
    
    // Available field types based on InputType enum
    const fieldTypes = [
        { type: 'text', label: 'Text Input', icon: 'T' },
        { type: 'email', label: 'Email', icon: '@' },
        { type: 'number', label: 'Number', icon: '#' },
        { type: 'textarea', label: 'Text Area', icon: 'P' },
        { type: 'select', label: 'Dropdown', icon: 'D' },
        { type: 'radio', label: 'Radio Button', icon: 'R' },
        { type: 'checkbox', label: 'Checkbox', icon: 'C' },
        { type: 'date', label: 'Date', icon: '📅' },
        { type: 'time', label: 'Time', icon: '⏰' },
        { type: 'file', label: 'File Upload', icon: 'F' }
    ];

    // Sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        fetchTemplates();
    }, []);
    
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/form-builder/templates/');
            setTemplates(response.data);
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        setIsCreating(true);
        setIsEditing(false);
        setPreviewMode(false);
        setLocalSelectedTemplate(null);
        setFormFields(reconcileFormFields('assignment', []));
        setFormConfig({
            name: '',
            description: '',
            form_type: 'assignment',
            submit_button_text: 'Submit',
            success_message: 'Form submitted successfully!',
            allow_multiple_submissions: false,
            is_active: true,
            max_submissions: null
        });
        setActiveTab('builder');
    };

    const handleEditTemplate = async (template) => {
        setIsEditing(true);
        setIsCreating(false);
        setPreviewMode(false); // Exit preview mode when editing
        setLocalSelectedTemplate(template);
        
        // Handle fields with proper ID generation
        const fieldsToSet = (template.fields || []).map(field => ({
            ...field,
            id: field.id || `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }));

        // Handle both old and new data structures
        const settings = template.settings || {};
        const resolvedFormType = template.form_type || 'assignment';

        // Re-inject/re-lock the fixed attendance fields regardless of what the
        // stored template has — covers legacy templates saved before this
        // existed, or a stored copy that lost its `locked` flag somehow.
        setFormFields(reconcileFormFields(resolvedFormType, fieldsToSet));
        setFormConfig({
            name: template.name || settings.title || '',
            description: template.description || settings.description || '',
            form_type: resolvedFormType,
            submit_button_text: template.submit_button_text || settings.submit_button_text || 'Submit',
            success_message: template.success_message || settings.success_message || 'Form submitted successfully!',
            // Normalised against the form type so a legacy template saved before the
            // policy existed can't display a value the server would refuse to honour.
            allow_multiple_submissions: resolveMultipleSubmissions(
                resolvedFormType,
                template.allow_multiple_submissions !== undefined
                    ? template.allow_multiple_submissions
                    : settings.allow_multiple_submissions
            ),
            is_active: template.is_active !== undefined ? template.is_active : true,
            max_submissions: template.max_submissions || null
        });
        setActiveTab('builder');
    };

    // Function to enter edit mode from preview mode
    const handleEnterEditMode = () => {
        setIsEditing(true);
        setPreviewMode(false);
    };

    // Function to go back from preview mode
    const handleGoBackFromPreview = () => {
        setPreviewMode(false);
        setIsEditing(false);
        setLocalSelectedTemplate(null);
        setActiveTab('templates');
    };

    const handlePreviewTemplate = (template) => {
        setLocalSelectedTemplate(template);
        
        // Handle fields for preview
        const fieldsToSet = (template.fields || []).map(field => ({
            ...field,
            id: field.id || `preview_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }));

        // Handle both old and new data structures
        const settings = template.settings || {};
        const resolvedFormType = template.form_type || 'assignment';

        setFormFields(reconcileFormFields(resolvedFormType, fieldsToSet));
        setFormConfig({
            name: template.name || settings.title || '',
            description: template.description || settings.description || '',
            form_type: resolvedFormType,
            submit_button_text: template.submit_button_text || settings.submit_button_text || 'Submit',
            success_message: template.success_message || settings.success_message || 'Form submitted successfully!',
            // Normalised against the form type so a legacy template saved before the
            // policy existed can't display a value the server would refuse to honour.
            allow_multiple_submissions: resolveMultipleSubmissions(
                resolvedFormType,
                template.allow_multiple_submissions !== undefined
                    ? template.allow_multiple_submissions
                    : settings.allow_multiple_submissions
            ),
            is_active: template.is_active !== undefined ? template.is_active : true,
            max_submissions: template.max_submissions || null
        });
        setPreviewMode(true);
        setIsEditing(false); // Ensure we're not in edit mode when previewing
        setActiveTab('builder'); // Go to builder tab in preview mode
    };

    // Handle drag end for sortable fields. Fixed attendance fields are never
    // part of the draggable set (see the render section), so this only ever
    // reorders the admin's own fields — they're just kept behind the fixed
    // ones when reassembling the array.
    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setFormFields((items) => {
                const locked = items.filter((item) => item.locked);
                const editable = items.filter((item) => !item.locked);
                const oldIndex = editable.findIndex(item => item.id === active.id);
                const newIndex = editable.findIndex(item => item.id === over.id);
                if (oldIndex === -1 || newIndex === -1) return items;

                return [...locked, ...arrayMove(editable, oldIndex, newIndex)];
            });
        }
    };

    // Add new field when clicking on field type
    const addNewField = (fieldType) => {
        const newField = createDefaultField(fieldType);
        setFormFields(prev => [...prev, newField]);
    };

    const createDefaultField = (type) => {
        const baseField = {
            id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            type,
            label: `New ${type} field`,
            placeholder: '',
            help_text: '',
            required: false,
            options: [],
            min_length: null,
            max_length: null,
            min_value: null,
            max_value: null
        };

        switch (type) {
            case 'select':
            case 'radio':
                return { 
                    ...baseField, 
                    options: [
                        { value: 'option1', label: 'Option 1' },
                        { value: 'option2', label: 'Option 2' }
                    ] 
                };
            case 'checkbox':
                return { 
                    ...baseField, 
                    options: [
                        { value: 'option1', label: 'Option 1' }
                    ] 
                };
            case 'number':
                return { ...baseField, min_value: 0, max_value: 100 };
            case 'text':
                return { ...baseField, min_length: null, max_length: 255 };
            case 'textarea':
                return { ...baseField, min_length: null, max_length: 1000 };
            default:
                return baseField;
        }
    };

    const updateField = (fieldId, updates) => {
        if (isAttendanceFixedFieldId(fieldId)) return; // fixed fields aren't editable
        setFormFields(prev => prev.map(field =>
            field.id === fieldId ? { ...field, ...updates } : field
        ));
    };

    const removeField = (fieldId) => {
        if (isAttendanceFixedFieldId(fieldId)) return; // fixed fields can't be removed
        setFormFields(prev => prev.filter(field => field.id !== fieldId));
    };

    const handleCancelClick = () => {
        // If in preview mode, go back to templates
        if (previewMode) {
            handleGoBackFromPreview();
            return;
        }

        const hasUnsavedChanges = formFields.length > 0 || 
                                  formConfig.name || 
                                  formConfig.description ||
                                  formConfig.form_type !== 'assignment';

        if (hasUnsavedChanges) {
            if (window.confirm('⚠️ Are you sure you want to cancel? All unsaved changes will be lost.')) {
                if (onClose) {
                    onClose();
                } else {
                    setActiveTab('templates');
                    setIsCreating(false);
                    setIsEditing(false);
                    setPreviewMode(false);
                    // Reset form state
                    setFormFields([]);
                    setFormConfig({
                        name: '',
                        description: '',
                        form_type: 'assignment',
                        submit_button_text: 'Submit',
                        success_message: 'Form submitted successfully!',
                        allow_multiple_submissions: false,
                        is_active: true,
                        max_submissions: null
                    });
                }
            }
        } else {
            if (onClose) {
                onClose();
            } else {
                setActiveTab('templates');
                setIsCreating(false);
                setIsEditing(false);
                setPreviewMode(false);
            }
        }
    };

    const saveTemplate = async () => {
        try {
            // Validate required fields
            if (!formConfig.name.trim()) {
                alert('Please enter a form name');
                return;
            }
            
            if (!formConfig.description.trim()) {
                alert('Please enter a form description');
                return;
            }
            
            if (formFields.length === 0) {
                alert('Please add at least one field to the form');
                return;
            }

            // The server enforces this too, but normalising here keeps the stored
            // template honest rather than saving a flag that is silently ignored.
            const allowMultiple = resolveMultipleSubmissions(
                formConfig.form_type,
                formConfig.allow_multiple_submissions
            );

            // Prepare the complete template data structure
            const templateData = {
                name: formConfig.name.trim(),
                description: formConfig.description.trim(),
                form_type: formConfig.form_type,
                allow_multiple_submissions: allowMultiple,
                fields: formFields.map((field, index) => ({
                    ...field,
                    id: field.id || `field_${Date.now()}_${index}`,
                    order: index + 1 // Ensure order is set for each field
                })),
                settings: {
                    title: formConfig.name.trim(),
                    description: formConfig.description.trim(),
                    submit_button_text: formConfig.submit_button_text || 'Submit',
                    success_message: formConfig.success_message || 'Form submitted successfully!',
                    allow_multiple_submissions: allowMultiple,
                    require_login: true, // Default to requiring login
                    send_confirmation_email: false // Default to not sending confirmation email
                },
                allowed_roles: ['student', 'preceptor', 'admin'], // Default allowed roles
                is_active: true, // Every template created or edited here is always active — no draft/inactive state in this UI
                is_published: false, // Default to not published
                max_submissions: formConfig.max_submissions || null
            };

            if (isEditing && localSelectedTemplate) {
                // For updates, ensure we don't send fields that shouldn't be updated
                const updateData = { ...templateData };
                delete updateData._id;
                delete updateData.created_by;
                delete updateData.created_at;
                
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Updating template with data:', updateData);
                const response = await axiosInstance.put(`/form-builder/templates/${localSelectedTemplate._id}`, updateData);
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Update response:', response.data);
            } else {
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Creating new template with data:', templateData);
                const response = await axiosInstance.post('/form-builder/templates/', templateData);
                if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Create response:', response.data);
            }

            await fetchTemplates();
            
            if (onClose) {
                onClose();
            } else {
                setActiveTab('templates');
                setIsCreating(false);
                setIsEditing(false);
            }
            
            // Reset form state
            setFormFields([]);
            setFormConfig({
                name: '',
                description: '',
                form_type: 'assignment',
                submit_button_text: 'Submit',
                success_message: 'Form submitted successfully!',
                allow_multiple_submissions: false,
                is_active: true,
                max_submissions: null
            });
            setLocalSelectedTemplate(null);
        } catch (error) {
            console.error('Error saving template:', error);
            // Use sanitized error message - never show raw backend errors
            alert(getActionErrorMessage(error, "save template"));
        }
    };

    const deleteTemplate = async (templateId) => {
        if (window.confirm('Are you sure you want to delete this template?')) {
            try {
                await axiosInstance.delete(`/form-builder/templates/${templateId}`);
                await fetchTemplates();
            } catch (error) {
                console.error('Error deleting template:', error);
            }
        }
    };

    const filteredTemplates = templates.filter(template => {
        const templateName = template.name || template.title || '';
        const templateDesc = template.description || '';
        const matchesSearch = templateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            templateDesc.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = filterType === 'all' || template.form_type === filterType;
        return matchesSearch && matchesType;
    });

    // Get unique form types from loaded templates
    const availableFormTypes = [...new Set(templates.map(template => template.form_type).filter(Boolean))];

    const publishedCount = templates.filter(template => template.is_published).length;
    const attendanceCount = templates.filter(template => template.form_type === 'attendance').length;
    const evaluationCount = templates.filter(template =>
        ['studenttopreceptorevaluation', 'preceptortostudentevaluation', 'assignmentevaluation']
            .includes(template.form_type)
    ).length;

    const renderTemplatesList = () => (
        <div>
            <div className="mc-hero">
                <div className="mc-hero__copy">
                    <span className="mc-eyebrow">Admin</span>
                    <h1>Form Builder</h1>
                    <p>Build assignment, attendance and evaluation forms, then attach them to a curriculum with a schedule.</p>
                </div>
                <div className="mc-hero__stats">
                    <div className="mc-stat"><strong>{templates.length}</strong><span>Templates</span></div>
                    <div className="mc-stat"><strong>{publishedCount}</strong><span>Published</span></div>
                    <div className="mc-stat"><strong>{evaluationCount}</strong><span>Evaluations</span></div>
                    <div className="mc-stat"><strong>{attendanceCount}</strong><span>Attendance</span></div>
                </div>
            </div>

            <div className="mc-toolbar">
                <div className="mc-search-box">
                    <FaSearch />
                    <input
                        type="text"
                        placeholder="Search templates by name or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="mr-status-filter"
                >
                    <option value="all">All types</option>
                    {availableFormTypes.map(type => (
                        <option key={type} value={type}>{getFormTypeRule(type).label}</option>
                    ))}
                </select>
                <div className="mc-toolbar-actions">
                    <button className="mc-secondary-btn" type="button" onClick={() => setShowAnalytics(true)}>
                        <FaChartBar /> Analytics
                    </button>
                    <button className="mc-secondary-btn" type="button" onClick={() => setShowSubmissions(true)}>
                        <FaClipboardList /> Submissions
                    </button>
                    <button className="mc-primary-btn" type="button" onClick={handleCreateNew}>
                        <FaPlus /> New Template
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="mc-loading-shell">
                    <LoadingSpinner text="Loading templates..." variant="inline" />
                </div>
            ) : filteredTemplates.length === 0 ? (
                <div className="mc-empty-state">
                    <FaFileAlt />
                    <h3>{templates.length === 0 ? 'No form templates yet' : 'No matching templates'}</h3>
                    <p>
                        {templates.length === 0
                            ? 'Create your first template to start collecting assignments, attendance or evaluations'
                            : 'Try a different search term or type filter'}
                    </p>
                </div>
            ) : (
                <div className="mc-cards-grid">
                    {filteredTemplates.map(template => {
                        const rule = getFormTypeRule(template.form_type);
                        return (
                            <div key={template._id} className="mc-curriculum-card">
                                <div className="mc-curriculum-card__top">
                                    <div>
                                        <h4>{template.name || template.title}</h4>
                                        <p className="mc-curriculum-card__rank">{rule.label}</p>
                                    </div>
                                    <div className={`mc-status-pill ${template.is_published ? 'is-status-active' : 'is-status-upcoming'}`}>
                                        {template.is_published ? 'Published' : 'Draft'}
                                    </div>
                                </div>

                                <div className="mc-curriculum-card__meta">
                                    <span><FaFileAlt /> {template.description || 'No description'}</span>
                                </div>

                                <div className="mc-card-metrics">
                                    <div className="mc-card-metric">
                                        <strong>{template.fields?.length || 0}</strong>
                                        <small>Fields</small>
                                    </div>
                                    <div className="mc-card-metric">
                                        <strong>{template.submission_count || 0}</strong>
                                        <small>Submissions</small>
                                    </div>
                                    <div className="mc-card-metric">
                                        <strong style={{ fontSize: '0.95rem' }}>
                                            {rule.multiple === 'never' ? 'Single' : 'Multiple'}
                                        </strong>
                                        <small>Per person</small>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                                    <button className="mc-secondary-btn" type="button" onClick={() => handlePreviewTemplate(template)}>
                                        <FaEye /> Preview
                                    </button>
                                    <button className="mc-secondary-btn" type="button" onClick={() => handleEditTemplate(template)}>
                                        <FaEdit /> Edit
                                    </button>
                                    <button className="mc-danger-btn" type="button" onClick={() => deleteTemplate(template._id)}>
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );

    const renderFormBuilder = () => (
        <div className="form-builder-container">
            <div className="builder-header">
                <h2>
                    {previewMode 
                        ? `Preview Form - ${formConfig.name || 'Untitled'}`
                        : isEditing 
                            ? 'Edit Template' 
                            : 'Create New Template'
                    }
                </h2>
                <div className="builder-actions">
                    {previewMode ? (
                        // Preview mode: Go Back and Edit buttons
                        <>
                            <button 
                                onClick={handleGoBackFromPreview}
                                className="btn-secondary"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            >
                                ← Go Back
                            </button>
                            <button 
                                onClick={handleEnterEditMode}
                                className="btn-primary"
                            >
                                Edit Template
                            </button>
                        </>
                    ) : (
                        // Edit/Create mode: Cancel, Preview, and Save buttons
                        <>
                            <button 
                                onClick={handleCancelClick}
                                className="btn-cancel"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={() => setPreviewMode(true)}
                                className="btn-secondary"
                            >
                                Preview
                            </button>
                            <button 
                                onClick={saveTemplate}
                                className="btn-primary"
                            >
                                {isEditing ? 'Update' : 'Create'} Template
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="form-config">
                <div className="config-section">
                    <h3>Basic Information</h3>
                    <div className="config-grid">
                        <div className="config-item">
                            <label>Template Name:</label>
                            {previewMode ? (
                                <div className="preview-display">{formConfig.name || 'Untitled'}</div>
                            ) : (
                                <input
                                    type="text"
                                    value={formConfig.name}
                                    onChange={(e) => setFormConfig(prev => ({ ...prev, name: e.target.value }))}
                                    className="config-input"
                                    placeholder="Enter template name"
                                />
                            )}
                        </div>
                        <div className="config-item full-width">
                            <label>Description:</label>
                            {previewMode ? (
                                <div className="preview-display">{formConfig.description || 'No description'}</div>
                            ) : (
                                <textarea
                                    value={formConfig.description}
                                    onChange={(e) => setFormConfig(prev => ({ ...prev, description: e.target.value }))}
                                    className="config-input"
                                    placeholder="Enter template description"
                                    rows="3"
                                />
                            )}
                        </div>
                        <div className="config-item">
                            <label>Form Type:</label>
                            {previewMode ? (
                                <div className="preview-display">{formConfig.form_type || 'assignment'}</div>
                            ) : (
                                <select
                                    value={formConfig.form_type}
                                    onChange={(e) => {
                                        const form_type = e.target.value;
                                        setFormConfig(prev => ({
                                            ...prev,
                                            form_type,
                                            // Keep the submission policy in step with the type so the
                                            // saved template can never contradict the server rule.
                                            allow_multiple_submissions: resolveMultipleSubmissions(
                                                form_type,
                                                prev.allow_multiple_submissions
                                            )
                                        }));
                                        // Switching to attendance injects the fixed study-type +
                                        // calendar fields; switching away drops them again.
                                        setFormFields(prev => reconcileFormFields(form_type, prev));
                                    }}
                                    className="config-input"
                                >
                                    {FORM_TYPES.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            )}
                            {!previewMode && (
                                <small className="config-hint">{getFormTypeRule(formConfig.form_type).hint}</small>
                            )}
                        </div>
                    </div>
                </div>



                <div className="config-section">
                    <h3>Form Settings</h3>
                    <div className="config-grid">
                        <div className="config-item">
                            <label>Submit Button Text:</label>
                            {previewMode ? (
                                <div className="preview-display">{formConfig.submit_button_text || 'Submit'}</div>
                            ) : (
                                <input
                                    type="text"
                                    value={formConfig.submit_button_text}
                                    onChange={(e) => setFormConfig(prev => ({ ...prev, submit_button_text: e.target.value }))}
                                    className="config-input"
                                />
                            )}
                        </div>
                        <div className="config-item">
                            <label>Success Message:</label>
                            {previewMode ? (
                                <div className="preview-display">{formConfig.success_message || 'Form submitted successfully!'}</div>
                            ) : (
                                <input
                                    type="text"
                                    value={formConfig.success_message}
                                    onChange={(e) => setFormConfig(prev => ({ ...prev, success_message: e.target.value }))}
                                    className="config-input"
                                />
                            )}
                        </div>
                        <div className="config-item">
                            <label>Max Submissions (optional):</label>
                            {previewMode ? (
                                <div className="preview-display">{formConfig.max_submissions || 'No limit'}</div>
                            ) : (
                                <input
                                    type="number"
                                    value={formConfig.max_submissions || ''}
                                    onChange={(e) => setFormConfig(prev => ({ ...prev, max_submissions: parseInt(e.target.value) || null }))}
                                    className="config-input"
                                    min="1"
                                />
                            )}
                        </div>
                        {/* Only assignment forms can opt into multiple submissions — every
                            other type has a fixed policy (see FORM_TYPES), so there's nothing
                            to toggle and no reason to show a disabled control for it. */}
                        {formConfig.form_type === 'assignment' && (
                            <div className="config-item toggle-item">
                                <label className="toggle-item-label">Allow Multiple Submissions</label>
                                {previewMode ? (
                                    <div className="preview-display">
                                        {formConfig.allow_multiple_submissions ? '✓ Enabled' : '✗ Disabled'}
                                    </div>
                                ) : (
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={formConfig.allow_multiple_submissions}
                                        className={`modern-toggle ${formConfig.allow_multiple_submissions ? 'is-on' : ''}`}
                                        onClick={() => setFormConfig(prev => ({
                                            ...prev,
                                            allow_multiple_submissions: !prev.allow_multiple_submissions
                                        }))}
                                    >
                                        <span className="modern-toggle-track">
                                            <span className="modern-toggle-thumb" />
                                        </span>
                                        <span className="modern-toggle-state">
                                            {formConfig.allow_multiple_submissions ? 'On' : 'Off'}
                                        </span>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {previewMode ? (
                <div className="form-preview">
                    <h3>Form Fields Preview</h3>
                    <div className="preview-form">
                        <h4>{formConfig.name || 'Untitled Form'}</h4>
                        <p>{formConfig.description || 'No description'}</p>
                        {formFields.length === 0 ? (
                            <div className="empty-form">
                                <p>No fields configured for this form</p>
                            </div>
                        ) : (
                            <div className="preview-fields-container">
                                {formFields.map(field => renderPreviewField(field))}
                                <button className="preview-submit-btn" disabled>
                                    {formConfig.submit_button_text || 'Submit'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <div className="builder-workspace">
                        <div className="field-palette">
                            <h3>Field Types</h3>
                            <div className="field-types-list">
                                {fieldTypes.map((fieldType) => (
                                    <DraggableFieldType
                                        key={fieldType.type}
                                        fieldType={fieldType}
                                        onAdd={addNewField}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="form-canvas">
                            <h3>Form Fields</h3>
                            <div className="form-fields-container">
                                {/* Fixed attendance fields always render first, outside the
                                    sortable set — they can't be dragged, edited, or removed. */}
                                {formFields.filter(f => f.locked).map((field) => (
                                    <LockedFieldRow key={field.id} field={field} />
                                ))}
                                {formFields.filter(f => !f.locked).length === 0 ? (
                                    formFields.length === 0 && (
                                        <div className="empty-form">
                                            <p>Click on field types to add them to your form</p>
                                        </div>
                                    )
                                ) : (
                                    <SortableContext
                                        items={formFields.filter(f => !f.locked).map(f => f.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        {formFields.filter(f => !f.locked).map((field, index) => (
                                            <SortableFieldEditor
                                                key={field.id}
                                                field={field}
                                                index={index}
                                                updateField={updateField}
                                                removeField={removeField}
                                            />
                                        ))}
                                    </SortableContext>
                                )}
                            </div>
                        </div>
                    </div>
                </DndContext>
            )}
        </div>
    );

    const renderPreviewField = (field) => (
        <div key={field.id} className="preview-field">
            <label className="preview-label">
                {field.label}
                {field.required && <span className="required">*</span>}
            </label>
            
            {field.help_text && (
                <small className="field-help-text">{field.help_text}</small>
            )}
            
            {field.type === 'text' && (
                <input type="text" placeholder={field.placeholder} className="preview-input" />
            )}
            
            {field.type === 'email' && (
                <input type="email" placeholder={field.placeholder} className="preview-input" />
            )}
            
            {field.type === 'number' && (
                <input 
                    type="number" 
                    placeholder={field.placeholder} 
                    className="preview-input"
                    min={field.min_value}
                    max={field.max_value}
                />
            )}
            
            {field.type === 'textarea' && (
                <textarea placeholder={field.placeholder} className="preview-input" rows="3" />
            )}
            
            {field.type === 'select' && (
                <select className="preview-input">
                    <option value="">Select an option</option>
                    {field.options?.map((option, i) => (
                        <option key={i} value={option.value || option}>
                            {option.label || option}
                        </option>
                    ))}
                </select>
            )}

            {field.type === 'attendance_calendar' && (
                <div className="preview-input preview-attendance-calendar">
                    📅 Multi-day calendar — students select any days across any month or year, each with its own start/end time
                </div>
            )}
            
            {field.type === 'radio' && (
                <div className="preview-radio-group">
                    {field.options?.map((option, i) => (
                        <label key={i} className="preview-radio">
                            <input type="radio" name={field.id} value={option.value || option} />
                            {option.label || option}
                        </label>
                    ))}
                </div>
            )}
            
            {field.type === 'checkbox' && (
                <div className="preview-checkbox-group">
                    {field.options?.map((option, i) => (
                        <label key={i} className="preview-checkbox">
                            <input type="checkbox" value={option.value || option} />
                            {option.label || option}
                        </label>
                    ))}
                </div>
            )}
            
            {field.type === 'date' && (
                <input type="date" className="preview-input" />
            )}
            
            {field.type === 'time' && (
                <input type="time" className="preview-input" />
            )}
            
            {field.type === 'file' && (
                <input type="file" className="preview-input" />
            )}
        </div>
    );

    return (
        <div className="form-builder">
            <div className="form-builder-tabs">
                <button
                    className={`tab ${activeTab === 'templates' ? 'active' : ''}`}
                    onClick={() => setActiveTab('templates')}
                >
                    Templates
                </button>
                {(isCreating || isEditing) && (
                    <button
                        className={`tab ${activeTab === 'builder' ? 'active' : ''}`}
                        onClick={() => setActiveTab('builder')}
                    >
                        Builder
                    </button>
                )}
                {previewMode && (
                    <button
                        className={`tab ${activeTab === 'preview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('preview')}
                    >
                        Preview
                    </button>
                )}
            </div>

            <div className="form-builder-content">
                {activeTab === 'templates' && renderTemplatesList()}
                {(activeTab === 'builder' || activeTab === 'preview') && renderFormBuilder()}
            </div>
            
            {/* Form Submissions Viewer */}
            {showSubmissions && (
                <FormSubmissionViewer 
                    templates={templates}
                    onClose={() => setShowSubmissions(false)}
                />
            )}
            
            {/* Form Analytics Viewer */}
            {showAnalytics && (
                <FormAnalytics 
                    templates={templates}
                    onClose={() => setShowAnalytics(false)}
                />
            )}
        </div>
    );
};

export default FormBuilder;
